import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import {
  catchError,
  concatMap,
  distinctUntilChanged,
  finalize,
  map,
  shareReplay,
  tap,
} from 'rxjs/operators';
import { LEGACY_FLAT_TO_CATALOG } from 'src/app/config/portal-permissions.constants';
import {
  PERMISSION_SOURCE_MODE,
  PermissionSourceMode,
} from 'src/app/config/permission-source.config';
import { AuthService, UserType } from 'src/app/modules/auth';
import {
  UserPermissionGrantDto,
  UserPermissionsApiService,
} from './user-permissions-api.service';
import { UserPermissionsResponseDto } from './user-permissions.models';
import { extractPermissionGrantsFromResponse } from './user-permissions.normalize';
import {
  grantAllowsRead,
  grantAllowsWrite,
  PermissionGrant,
} from './permission-grant.util';

/**
 * Uygulama genelinde permission kontrolü (sidebar, route, buton).
 *
 * Kaynak 1: login permissions[] — düz key listesi
 * Kaynak 2: UserPermissions API — { permissionKey, read, write }[]
 */
const SUPER_ADMIN_ROLE = 'SuperAdmin';

@Injectable({ providedIn: 'root' })
export class PermissionService {
  private grants = new Map<string, PermissionGrant>();
  private grantsLoaded = false;
  private grantsApiAvailable = false;
  private lastPermissionsResponse: UserPermissionsResponseDto | null = null;
  private readonly grantsReady$ = new BehaviorSubject<boolean>(false);
  /**
   * Ayni anda gelen birden fazla loadGrantsFromApi() cagrisini (otomatik
   * currentUser$ senkronizasyonu + company/application switch sonrasi elle
   * cagrilan yerler) tek UserPermissions HTTP istegine indirger.
   */
  private grantsLoadInFlight$: Observable<UserPermissionGrantDto[]> | null =
    null;

  constructor(
    private authService: AuthService,
    private userPermissionsApi: UserPermissionsApiService,
  ) {
    this.bootstrapGrantsSync();
  }

  get sourceMode(): PermissionSourceMode {
    return PERMISSION_SOURCE_MODE;
  }

  get grantsReady(): Observable<boolean> {
    return this.grantsReady$.asObservable();
  }

  /** Grants/login yüklendikten sonra HTTP interceptor devreye girer */
  get isEnforcementReady(): boolean {
    if (!this.authService.currentUserValue?.token) {
      return false;
    }
    return this.grantsReady$.getValue();
  }

  /** Son UserPermissions API yanıtı (applicationId, roles, permissions) */
  get lastUserPermissions(): UserPermissionsResponseDto | null {
    return this.lastPermissionsResponse;
  }

  /** DevTools: `ng.probe($0).injector.get(PermissionService).debugState` */
  get debugState(): {
    source: 'grants' | 'login';
    grantsLoaded: boolean;
    grantsApiAvailable: boolean;
    grantCount: number;
    grants: Record<string, PermissionGrant>;
    loginPermissionKeys: string[];
  } {
    const grants: Record<string, PermissionGrant> = {};
    this.grants.forEach((value, key) => {
      grants[key] = value;
    });
    return {
      source: this.useGrantsSource() ? 'grants' : 'login',
      grantsLoaded: this.grantsLoaded,
      grantsApiAvailable: this.grantsApiAvailable,
      grantCount: this.grants.size,
      grants,
      loginPermissionKeys: [...this.permissionKeys],
    };
  }

  /** Login'den gelen düz permission key listesi */
  get permissionKeys(): string[] {
    return this.authService.currentUserValue?.permissions ?? [];
  }

  /**
   * Grants API'den yükle. Login / refresh / company switch sonrası çağrılır.
   *
   * Otomatik senkronizasyon (bootstrapGrantsSync) ile company/application
   * switch sonrası elle yapılan çağrılar (company-select, navbar preview reset)
   * aynı ana denk gelebilir; ikisi de aynı anda çalışıyorsa tek HTTP isteği
   * paylaşılır (refreshUserData'daki userRefreshInFlight$ ile aynı desen).
   */
  loadGrantsFromApi(): Observable<UserPermissionGrantDto[]> {
    if (!this.userPermissionsApi.isEnabled()) {
      this.clearGrantsState();
      this.grantsReady$.next(true);
      return of([]);
    }

    if (this.grantsLoadInFlight$) {
      return this.grantsLoadInFlight$;
    }

    this.grantsReady$.next(false);

    this.grantsLoadInFlight$ = this.userPermissionsApi.getCurrentUserPermissions().pipe(
      tap((response) => {
        const grants = extractPermissionGrantsFromResponse(response);
        this.lastPermissionsResponse = response;
        if (grants.length > 0) {
          this.applyGrants(grants);
          this.grantsApiAvailable = true;
        } else {
          this.grants.clear();
          this.grantsApiAvailable = false;
        }
        this.grantsLoaded = true;
        this.grantsReady$.next(true);
      }),
      catchError(() => {
        this.clearGrantsState();
        this.grantsReady$.next(true);
        return of(null);
      }),
      map((response) => response?.permissions ?? []),
      finalize(() => {
        this.grantsLoadInFlight$ = null;
      }),
      shareReplay(1),
    );

    return this.grantsLoadInFlight$;
  }

  invalidateGrants(): void {
    this.clearGrantsState();
    this.grantsReady$.next(false);
  }

  hasRead(permissionKey: string): boolean {
    if (!permissionKey || this.isSuperAdmin()) {
      return true;
    }

    if (this.useGrantsSource()) {
      return this.hasReadFromGrants(permissionKey);
    }

    return this.hasReadFromLogin(permissionKey);
  }

  hasWrite(permissionKey: string): boolean {
    if (!permissionKey || this.isSuperAdmin()) {
      return true;
    }

    if (this.userPermissionsApi.isEnabled()) {
      if (!this.grantsReady$.getValue()) {
        return false;
      }
      if (this.useGrantsSource()) {
        return this.hasWriteFromGrants(permissionKey);
      }
      // Grants API aktifken yanit alinamadi/bos geldiyse read=write varsaymak,
      // read-only kullanicilara yazma butonlarini acar (backend 403'ler ama UI
      // yanlis gorunur). Muhafazakar davran: write yok say. hasRead'in login
      // fallback'i sayfa gorunurlugunu koruduğu icin kullanici kilitlenmez.
      return false;
    }

    // Grants API kapali (legacy ortam): duz listede read/write ayrimi yoktur.
    return this.hasReadFromLogin(permissionKey);
  }

  hasAnyRead(...keys: string[]): boolean {
    return keys.some((k) => this.hasRead(k));
  }

  hasAllRead(...keys: string[]): boolean {
    return keys.every((k) => this.hasRead(k));
  }

  hasAnyWrite(...keys: string[]): boolean {
    return keys.some((k) => this.hasWrite(k));
  }

  hasAnyUnderModule(moduleKey: string, featureKeys: readonly string[]): boolean {
    if (this.hasRead(moduleKey)) {
      return true;
    }
    return featureKeys.some((k) => this.hasRead(k));
  }

  /** Modül key'i veya alt key'lerden biri (prefix) */
  hasReadUnderKeyPrefix(prefix: string): boolean {
    if (!prefix || this.isSuperAdmin()) {
      return true;
    }
    if (this.useGrantsSource()) {
      for (const [key, grant] of this.grants.entries()) {
        if (
          (key === prefix || key.startsWith(`${prefix}.`)) &&
          (grant.read || grant.write)
        ) {
          return true;
        }
      }
      return false;
    }
    return this.permissionKeys.some(
      (k) => k === prefix || k.startsWith(`${prefix}.`),
    );
  }

  private bootstrapGrantsSync(): void {
    if (!this.userPermissionsApi.isEnabled()) {
      this.grantsReady$.next(true);
      return;
    }

    // HttpClient/interceptor zinciri PermissionService oluştururken tetiklenmesin
    queueMicrotask(() => {
      this.authService.currentUser$
        .pipe(
          distinctUntilChanged(
            (prev, curr) =>
              this.permissionLoadContextKey(prev) ===
              this.permissionLoadContextKey(curr),
          ),
          concatMap((user) => {
            if (!user?.token) {
              this.invalidateGrants();
              this.grantsReady$.next(true);
              return of([]);
            }
            return this.loadGrantsFromApi();
          }),
        )
        .subscribe();
    });
  }

  /** UserRefreshData sonrası attachment zenginleştirmesi aynı izin bağlamı — tekrar API çağırma */
  private permissionLoadContextKey(user: UserType): string {
    if (!user?.token) {
      return '';
    }
    const roles = [...(user.roles ?? [])].sort().join(',');
    return [user.token, user.branchId ?? '', roles].join('|');
  }

  /**
   * SuperAdmin rolunun DB'de gercek RolePermission kaydi yoktur (backend BypassRoles
   * ile tum izin kontrollerini atlar). Frontend'de de ayni davranisi saglamak icin
   * permission-key kontrollerinden once rol bazli bypass uygulanir.
   */
  private isSuperAdmin(): boolean {
    return !!this.authService.currentUserValue?.roles?.includes(SUPER_ADMIN_ROLE);
  }

  private useGrantsSource(): boolean {
    return (
      this.userPermissionsApi.isEnabled() &&
      this.grantsLoaded &&
      this.grantsApiAvailable &&
      this.grants.size > 0
    );
  }

  private hasReadFromGrants(permissionKey: string): boolean {
    if (grantAllowsRead(this.grants, permissionKey)) {
      return true;
    }
    return this.hasLegacyFlatGrantForCatalogKey(permissionKey);
  }

  private hasWriteFromGrants(permissionKey: string): boolean {
    if (grantAllowsWrite(this.grants, permissionKey)) {
      return true;
    }
    return false;
  }

  private hasReadFromLogin(permissionKey: string): boolean {
    const flat = this.permissionKeys;
    if (flat.includes(permissionKey)) {
      return true;
    }
    return this.hasLegacyFlatGrantForCatalogKey(permissionKey);
  }

  private hasLegacyFlatGrantForCatalogKey(catalogKey: string): boolean {
    const flat = this.permissionKeys;
    for (const [legacyKey, catalogKeys] of Object.entries(
      LEGACY_FLAT_TO_CATALOG,
    )) {
      if (catalogKeys.includes(catalogKey) && flat.includes(legacyKey)) {
        return true;
      }
    }
    return false;
  }

  private applyGrants(items: UserPermissionGrantDto[]): void {
    this.grants.clear();
    for (const item of items ?? []) {
      if (!item?.permissionKey) {
        continue;
      }
      this.grants.set(item.permissionKey, {
        read: !!item.read,
        write: !!item.write,
      });
    }
  }

  private clearGrantsState(): void {
    this.grants.clear();
    this.grantsLoaded = false;
    this.grantsApiAvailable = false;
    this.lastPermissionsResponse = null;
  }
}
